import { useState } from "react";
import { useStore } from "../store.ts";
import { createEngine } from "../engine/audio.ts";
import {
  calibrate,
  calibrationVerdict,
  looksWireless,
  type CalibrationProgress,
} from "../engine/calibration.ts";

/**
 * Mandatory latency calibration.
 *
 * This screen refuses to let you past it if the output looks wireless or the
 * click can't be heard. That is deliberate: a score computed on an
 * uncalibrated or drifting audio path is a measurement of the hardware, not
 * the player, and reporting it would poison exactly the question this
 * prototype exists to answer.
 */
export function Calibrate() {
  const store = useStore();
  const [progress, setProgress] = useState<CalibrationProgress | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outputLabel, setOutputLabel] = useState("");
  const [confirmedHeadphones, setConfirmedHeadphones] = useState(false);

  async function run() {
    setBusy(true);
    setError(null);
    try {
      let engine = useStore.getState().engine;
      if (!engine) {
        engine = await createEngine(store.config);
        useStore.getState().set({ engine });
      }

      // The Web Audio API exposes no transport info for the OUTPUT device, so
      // we ask the browser for the output list and fall back to the user's own
      // description. The latency ceiling is the real backstop.
      let label = outputLabel;
      if (!label) {
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          label =
            devices.find((d) => d.kind === "audiooutput" && d.deviceId === "default")
              ?.label ?? "";
        } catch {
          label = "";
        }
      }

      const result = await calibrate({
        ctx: engine.ctx,
        capture: engine.capture,
        config: store.config,
        outputLabel: label || "unknown output",
        suspectedWireless: looksWireless(`${label} ${engine.inputLabel}`),
        onProgress: setProgress,
      });

      const verdict = calibrationVerdict(result, store.config);
      useStore.getState().set({
        calibration: result,
        calibrationBlocked: verdict.ok ? null : (verdict.reason ?? null),
      });
    } catch (e) {
      setError(
        e instanceof Error
          ? `${e.message}. Microphone access is required — check the browser's permission prompt.`
          : String(e),
      );
    } finally {
      setBusy(false);
      setProgress(null);
    }
  }

  const cal = store.calibration;
  const blocked = store.calibrationBlocked;

  return (
    <>
      <div className="panel">
        <h2>Headphones required</h2>
        <p className="sub">
          In every mode where audio plays while you sing, speaker output bleeds into
          the microphone and the onset detector hears the recording instead of you.
          Wired headphones only — Bluetooth adds 150-300ms of drifting delay that no
          single calibration constant can correct.
        </p>
        <label className="row" style={{ cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={confirmedHeadphones}
            onChange={(e) => setConfirmedHeadphones(e.target.checked)}
          />
          <span>I'm wearing wired headphones.</span>
        </label>
      </div>

      <div className="panel">
        <h2>Latency calibration</h2>
        <p className="sub">
          catchup! plays {store.config.capture.calibrationClicks} short clicks and
          records them back through your mic. The delay between sending and hearing
          each one is your round-trip latency; it gets subtracted from every onset
          you sing. Keep the room quiet and don't move the mic.
        </p>

        <button
          className="primary"
          disabled={busy || !confirmedHeadphones}
          onClick={() => void run()}
        >
          {busy ? "Listening…" : cal ? "Re-run calibration" : "Start calibration"}
        </button>

        {progress && (
          <>
            <div className="bar">
              <div style={{ width: `${(progress.click / progress.total) * 100}%` }} />
            </div>
            <p className="sub" style={{ margin: 0 }}>
              Click {progress.click} of {progress.total}
              {progress.measured !== undefined &&
                ` — ${(progress.measured * 1000).toFixed(0)}ms (confidence ${progress.confidence?.toFixed(2)})`}
            </p>
          </>
        )}

        {error && <p className="note bad">{error}</p>}

        {cal && (
          <div className="stats">
            <div className="stat">
              <div className="k">Round-trip latency</div>
              <div className="v">{(cal.latency * 1000).toFixed(0)}ms</div>
            </div>
            <div className="stat">
              <div className="k">Confidence</div>
              <div className="v">{cal.confidence.toFixed(2)}</div>
            </div>
            <div className="stat">
              <div className="k">Sample rate</div>
              <div className="v">{(cal.sampleRate / 1000).toFixed(1)}k</div>
            </div>
          </div>
        )}

        {blocked && <p className="note bad">{blocked}</p>}
        {cal && !blocked && (
          <p className="note good">
            Calibrated. Every onset you sing will be shifted{" "}
            {(cal.latency * 1000).toFixed(0)}ms earlier to account for the round trip.
          </p>
        )}
      </div>

      <div className="row">
        <button onClick={() => useStore.getState().backToLibrary()}>← Library</button>
        <button
          className="primary"
          disabled={!cal || !!blocked}
          onClick={() => useStore.getState().set({ screen: "pick-round" })}
        >
          Continue
        </button>
      </div>
    </>
  );
}
